// Pure preview helper for the FulfillmentEntryBulkImportModal.
//
// Parses pasted text (CSV or JSON), validates rows, and partitions
// them into valid / skipped. Mirrors the manufacturing pattern in
// `src/lib/manufacturing/bulkImportPreview.ts` but for *units* not
// *materials* -- each row creates one mfg_unit, no name-match-update
// logic needed.
//
// The server is the source of truth; this preview is a UX courtesy
// before the operator commits.

import { isMfgUnitOrigin, type MfgUnitOrigin } from "./originEnum";

export const REQUIRED_KEYS = ["origin"] as const;
export const OPTIONAL_KEYS = [
  "plant_id",
  "notes",
  "intake_template_slug",
] as const;
export const RECOGNIZED_KEYS = new Set<string>([...REQUIRED_KEYS, ...OPTIONAL_KEYS]);

export type ParsedEntryRow = {
  idx: number;
  origin: MfgUnitOrigin;
  plant_id?: string;
  notes?: string;
  intake_template_slug?: string;
};

export type FulfillmentImportFormat = "csv" | "json";

export type FulfillmentBulkPreview =
  | { kind: "empty" }
  | { kind: "parse-error"; message: string }
  | {
      kind: "ok";
      format: FulfillmentImportFormat;
      total: number;
      validRows: ParsedEntryRow[];
      skipped: Array<{ idx: number; reason: string }>;
      ignoredKeys: string[];
    };

function tryParseJson(raw: string): Array<Record<string, unknown>> | { error: string } {
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed as Array<Record<string, unknown>>;
    if (parsed && typeof parsed === "object" && Array.isArray((parsed as { rows?: unknown }).rows)) {
      return (parsed as { rows: Array<Record<string, unknown>> }).rows;
    }
    return { error: "JSON must be an array or an object with a `rows` array" };
  } catch (e) {
    return { error: e instanceof Error ? e.message : "invalid JSON" };
  }
}

function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cur += c;
      }
    } else {
      if (c === ",") {
        out.push(cur);
        cur = "";
      } else if (c === '"') {
        inQuotes = true;
      } else {
        cur += c;
      }
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

function parseCsv(raw: string): Array<Record<string, unknown>> | { error: string } {
  const lines = raw.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return { error: "CSV needs a header row and at least one data row" };
  const headers = parseCsvLine(lines[0]);
  const rows: Array<Record<string, unknown>> = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cells = parseCsvLine(lines[i]);
    const obj: Record<string, unknown> = {};
    for (let j = 0; j < headers.length; j += 1) {
      if (j >= cells.length) break;
      obj[headers[j]] = cells[j];
    }
    rows.push(obj);
  }
  return rows;
}

export function buildFulfillmentBulkPreview(rawText: string): FulfillmentBulkPreview {
  const trimmed = rawText.trim();
  if (!trimmed) return { kind: "empty" };

  const format: FulfillmentImportFormat = trimmed.startsWith("[") || trimmed.startsWith("{") ? "json" : "csv";
  const parsed = format === "json" ? tryParseJson(trimmed) : parseCsv(trimmed);
  if (!Array.isArray(parsed)) return { kind: "parse-error", message: parsed.error };
  if (parsed.length === 0) return { kind: "parse-error", message: "no rows found" };

  const validRows: ParsedEntryRow[] = [];
  const skipped: Array<{ idx: number; reason: string }> = [];
  const ignoredKeys = new Set<string>();

  parsed.forEach((obj, i) => {
    for (const k of Object.keys(obj)) if (!RECOGNIZED_KEYS.has(k)) ignoredKeys.add(k);

    const rawOrigin = typeof obj.origin === "string" ? obj.origin.trim() : "";
    if (!isMfgUnitOrigin(rawOrigin) || rawOrigin === "manufactured") {
      skipped.push({
        idx: i,
        reason: "origin must be cash_purchase or repo_intake",
      });
      return;
    }

    const row: ParsedEntryRow = { idx: i, origin: rawOrigin };
    if (typeof obj.plant_id === "string" && obj.plant_id.trim()) {
      row.plant_id = obj.plant_id.trim();
    }
    if (typeof obj.notes === "string" && obj.notes.trim()) {
      row.notes = obj.notes.trim();
    }
    if (typeof obj.intake_template_slug === "string" && obj.intake_template_slug.trim()) {
      row.intake_template_slug = obj.intake_template_slug.trim();
    }
    validRows.push(row);
  });

  return {
    kind: "ok",
    format,
    total: parsed.length,
    validRows,
    skipped,
    ignoredKeys: Array.from(ignoredKeys).sort(),
  };
}
